import { useContext, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DataField, Divider, P, TealSubSection } from "./common-components";
import { FormHeader } from "./FormHeader";
import { FormContextMA } from "../form-context";
import { GetPatientAddress } from "../common/GetPatientAddress";
import { ClinicalServiceTypes } from "../../patient/text/data";
import {
  formatHealth,
  formatPhoneDocumentation,
  formatToYYYY_MMM_DD,
} from "../../../helpers/functions";
import { FormFooter } from "./FormFooter";
import { AlertConfirmationTypes, GetAlertConfirmation } from "../../../helpers/constant";
import { GetAllReferrals } from "../common/GetAllReferrals";
import { getCurrentUserDetails } from "../../../redux/modules/user/actions";

export const BillForService = ({}) => {
  const { patient, clinicalServiceName, startDateTime, treatment, alertData, formType } =
    useContext(FormContextMA);

  const { currentLoggedInUserDetail } = useSelector((state) => state.currentLoggedInUserDetail);
  const usersState = useSelector((state) => state.users);
  const dispatch = useDispatch();

  // Helper to compute pharmacist full name once
  const getFullName = (u) => (u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : null);
  const pharmacistName =
    getFullName(currentLoggedInUserDetail) ??
    getFullName(usersState?.currentLoggedInUserDetail) ??
    "-";

  // Helper to format signature URL
  const getSignatureUrl = (u) => {
    if (!u?.signature || typeof u.signature !== 'string' || u.signature.length === 0) return null;
    return u.signature.startsWith('data:') ? u.signature : `data:image/png;base64,${u.signature}`;
  };
  const pharmacistSignature =
    getSignatureUrl(currentLoggedInUserDetail) ??
    getSignatureUrl(usersState?.currentLoggedInUserDetail) ??
    null;

  // Helper to get license number
  const getLicenseNumber = (u) => {
    if (!u?.driverLicence || typeof u.driverLicence !== 'string' || u.driverLicence.trim().length === 0) return "";
    return u.driverLicence.trim();
  };
  const pharmacistLicenseNumber =
    getLicenseNumber(currentLoggedInUserDetail) ||
    getLicenseNumber(usersState?.currentLoggedInUserDetail) ||
    "";

  // Fetch user details if not available
  useEffect(() => {
    if (!currentLoggedInUserDetail) {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      const userId = userInfo.userId;
      
      if (userId) {
        dispatch(getCurrentUserDetails(userId, false));
      }
    }
  }, [currentLoggedInUserDetail, dispatch]);

  return (
    <>
      <FormHeader formType={formType} alertData={alertData} />
      {/* patient details */}
      <div className="px-9 py-4 space-y-8">
        <div className="flex flex-row justify-between font-medium">
          <P>
            <span className="text-REELTeal">Service Date</span>{" "}
            {formatToYYYY_MMM_DD(startDateTime)}
          </P>
        </div>
        <div className="flex flex-col gap-2">
          <DataField
            value={(
              (patient.firstName || "") +
              (patient.middleName ? ` ${patient.middleName}` : "") +
              (patient.lastName ? ` ${patient.lastName}` : "")
            ).trim()}
          />
          <DataField
            value={
              patient?.provincialInsurance?.cardNumber
                ? `${formatHealth(patient.provincialInsurance.cardNumber)}-${
                    patient.provincialInsurance.versionCode
                  }`
                : "XXX-XXX-XXX-EF"
            }
          />
          {patient?.contactInfo?.primaryPhone ? (
            <DataField
              value={
                patient?.contactInfo?.primaryPhone
                  ? formatPhoneDocumentation(patient.contactInfo.primaryPhone)
                  : ""
              }
            />
          ) : null}
          <DataField
            value={
              patient.dateOfBirth ? formatToYYYY_MMM_DD(patient.dateOfBirth) : ""
            }
          />
          <GetPatientAddress patient={patient} showLabel={false} />
        </div>
      </div>
      <Divider />
      {/* rx details */}
      <div className="px-9 py-4 flex flex-col gap-[0.342500in] font-medium min-h-[368px]">
        <div className="flex flex-row items-center">
          <p className="font-semibold text-base text-REELTeal">
            {clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
              ? "Allergic Rhinitis"
              : "Annual Medication Review"}
          </p>
        </div>
        <DataField
          label={`This document confirms a Minor Ailment Service for ${
            clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
              ? "Allergic Rhinitis"
              : "Annual Medication Review"
          } was provided at ${"Pharmacy Name"} , ${"Store#"}. As a result of the Assessment, the pharmacist advises the patient to seek medical attention for further evaluation.`}
        />
        <GetAllReferrals confirmationType={AlertConfirmationTypes.EndService} />
      </div>
      <Divider />
      {/* pharmacist details */}
      <div className="px-9 py-4 flex flex-col gap-[0.114167in]">
        <DataField
          vertical={true}
          labelTeal={true}
          label={"Prescribing Pharmacist Name"}
          value={pharmacistName}
        />
        <DataField
          vertical={true}
          labelTeal={true}
          label={"Pharmacist Signature"}
          value={
            pharmacistSignature ? (
              <img
                src={pharmacistSignature}
                alt="Pharmacist Signature"
                className="max-h-[40px] max-w-[200px] object-contain align-middle"
              />
            ) : (
              ""
            )
          }
        />
        <DataField
          vertical={true}
          labelTeal={true}
          label={"Pharmacist License"}
          value={pharmacistLicenseNumber}
        />
      </div>
      <Divider />
      {/* footer details */}
      <div className="px-9 py-4 flex flex-col gap-3">
        <div>
          <DataField
            vertical={true}
            labelTeal={true}
            label={"Rx Details"}
            value={"No Prescription Issued In Person - PIN 9858182"}
          />
          <DataField
            className="italic"
            label={
              "The Pharmacist billing for the Minor Ailment Service is responsible for ensuring the accuracy of the PIN generated by healthcompassRX."
            }
          />
        </div>

        <Divider />
        <DataField value={"No prescriptions issued - Referral information only"} />
        {!patient.provincialInsurance && <DataField
          className="text-red-400"
          value={
            "No Provincial Insurance - Pays for Clinical Services"
          }
        />}
      </div>
      <FormFooter />
    </>
  );
};
